import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, jobKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { normalizeInvoice } from './useInvoice';
import type { Invoice, UpdateInvoiceDto } from '@/types/invoice';

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<Invoice, UpdateInvoiceDto>({
    mutationFn: async ({ id, ...rest }) =>
      normalizeInvoice(
        await apiPatch<Record<string, unknown>, Omit<UpdateInvoiceDto, 'id'>>(
          operationsClient,
          invoiceEndpoints.update(id),
          rest,
        ),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData<Invoice>(invoiceKeys.detail(data.id), (current) =>
        current
          ? {
              ...current,
              ...data,
              contact: data.contact ?? current.contact,
            }
          : data,
      );
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(data.id) });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      if (data.jobId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(data.jobId) });
      }
    },
    showErrorToast: true,
  });
}
